export const subFlowDocumentationMarkdown = `
## What does this block do?

The Subflow block allows you to use a flow (child) within a larger flow (parent). 
This allows for breaking down complex processes into smaller units, 
making it easier to maintain and build intricate flows. 
Before using this block, make sure you have created your subflow to use in the parent flow.

## How to use this block

1.  Enter the flow to use as the child
2.  Select which version of the flow to use:
    *   **Published**: 
        This allows you to always run the latest published version of the specified flow
    *   **Specific Version**: 
        This allows you to select a specific version run with this flow 
        and it will always be used irrespective of the state of the specified version. 
        If you choose this version, you will be asked to specify which version.

        Note: You can NOT publish a flow if it includes subflows that are pinned to unpublished subflow versions.

3.  Next, add input values. 
    These are the values for the subflow’s input parameters and can be provided in different ways:
    * **Block**: The data from a selected block
      
      Choose an upstream block whose output (\`data\`) is to be used when this flow runs.
      For example, if the block outputs a patient object, that patient object will be used as the input value.
      A Script block can be used to construct complex values.
      
    * **Reference**: The value of a specified block, input, or subfield thereof

      Use this option to access data available when the flow runs,
      using the same syntax as in a Script block.
      For example, for a block named "patient", its entire data can be obtained using \`patient.data\`,
      or you may access a specific field such as \`patient.data.dateOfBirth\`. 
      This flow’s own inputs (if any) can be accessed like \`inputs.minimumAge\`. 

    * **Constant**: A fixed value specified here

      Enter a value in JSON format, such as:
      * A number, e.g., \`60\`
      * A boolean, e.g., \`true\` or \`false\`
      * Text (using quotes), e.g., \`"Pending"\`
      * An object, e.g., \`{ "page": 2, "limit": 100 }\`
      * An array, e.g., \`["CA", "NY", "TX"]\`

4. The output of this block will be the output of the subflow, if any.

## How to make a subflow

For another flow to be useful as a subflow, its inputs and outputs must be defined. 

1. In the subflow’s Start block, specify any inputs that the subflow will use,
   including a name and description.
   For example, you can define inputs named \`minimumAge\` and \`maximumAge\`.
   (See the Start block documentation for more information.)
2. Within the subflow, use those inputs with the syntax \`inputs.minimumAge\`, for example.
   When the subflow is later run, the input value supplied by its parent flow will be filled in.
3. Finally, add a single Output block to return a value to the parent flow when the subflow is finished.
   The Output block itself returns the value from one specified block, 
   although a Script block may be used to construct a complex object.
   (See the Output block documentation for more information.)
`;
